import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from "react-redux"

import { DropDownField, TextInputField } from "../../../helpers"
import Loading from '../../../common/Loading'
import Button from "../../../helpers/Button"
import { Menu } from 'primereact/menu'
import { utils, write } from "xlsx"
import Papa from "papaparse"

import { fetchBuyerRateContract, updateSearchOptions, updateSearchParams } from '../../../services/redux/reducers/buyerRateContract/buyerRateContract'
import { raiseToast } from '../../../services/redux/reducers/toast/toast'
import { getBuyerRateContracts } from '../../../services/api/buyerRateContracts'
import { getAddFilterOptions } from '../../../utils'


const buyerRCSearchConfiguration = [
    {
        srNO: 1,
        static: true,
        type: "SEARCH_BY",
        name: "SEARCH_BY",
        label: "Search by",
        options: [
            { label: 'No Filters', value: 'all' },
            { label: 'Branch', value: 'branch' },
            { label: 'Company ERPCode', value: 'companyERPCode' },
            { label: 'Material Id', value: 'materialId' },
            { label: 'Product Name', value: 'companyProdName' },
            { label: 'Specifications', value: 'companyProdSpec' },
            { label: 'Make', value: 'make' },
            { label: 'Part No', value: 'partNo' }
        ]
    }
]

const Search = (props) => {
    const { companyId, statusList, rcDataTypes, pmCompanyConfig } = props

    const [searchConfig, setSearchConfig] = useState(buyerRCSearchConfiguration)
    const [isLoading, setIsLoading] = useState(false)

    const paginationParams = useSelector(state => state.buyerRateContract.paginationParams)
    const searchParams = useSelector(state => state.buyerRateContract.searchParams)
    const searchOptions = useSelector(state => state.buyerRateContract.searchOptions)
    const isPageLoading = useSelector(state => state.buyerRateContract.loading)

    const filterMenu = useRef(null)
    const downloadMenu = useRef(null)
    const dispatch = useDispatch()

    useEffect(() => {
        const filteredSearchOptions = buyerRCSearchConfiguration.filter(eachOption => eachOption.static)
        dispatch(updateSearchOptions(filteredSearchOptions))
    }, [])

    const addFilterOption = (srNO) => {
        const relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)
        dispatch(updateSearchOptions([...searchOptions, relatedConfig]))
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = { ...searchParams }

        if (relatedSearchOption.type === "SEARCH_BY") {
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "DATE") {
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "SELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value
        const updatedSearchParams = { ...searchParams, [event.target.name]: event.target.value }

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }
        
        dispatch(updateSearchParams(updatedSearchParams))
    }

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const handleSearch = () => {
        if (!searchParams.searchBy) {
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please select search by' }))
        }
        if (!searchParams.searchText && searchParams.searchBy != 'all') {
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please enter search text' }))
        }
        dispatch(fetchBuyerRateContract({ ...paginationParams, ...searchParams, pageNo: 0 }))
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadRateContractDataSheet("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadRateContractDataSheet("CSV")
        },
    ]

    const downloadRateContractDataSheet = async (dataFormat) => {
        setIsLoading(true)
        const buyerRateContractResponse = await getBuyerRateContracts({ companyIds: [companyId], ...searchParams, paginator: false, include: [{ model: "CompanyBranches" }, { model: "CompanyERPCodeMap" }] })

        setIsLoading(false)
        if (buyerRateContractResponse.failure) {
            dispatch(raiseToast({ severity: 'error', detail: buyerRateContractResponse?.data?.error, life: 3000 }))
        } else {
            let rateContractProducts = []
            
            const rcKeyLabelMapper = {}
            rcDataTypes.map(eachDataType => {
                if (eachDataType.keyName === 'companyHSNOverride' || eachDataType.keyName === 'companyGSTOverride'){
                    if (pmCompanyConfig.HSNEntryAllowed){
                        rcKeyLabelMapper[eachDataType.keyName] = eachDataType.columnName
                    }
                }else{
                    rcKeyLabelMapper[eachDataType.keyName] = eachDataType.columnName
                }
            })
            const keyNames = Object.keys(rcKeyLabelMapper)

            buyerRateContractResponse.data?.data.map((eachRateContract, index) => {
                let branchName = ""
                if (eachRateContract.companyBranches) {
                    if(eachRateContract?.companyBranches[0]?.branchName){
                        branchName = eachRateContract?.companyBranches[0]?.branchName
                    }else{
                        branchName = "ALL"
                    }
                }

                let rateContractStatus = statusList.find(eachStatus => eachStatus.statusId === eachRateContract.statusId)

                const updatedRateContractObj = {
                    ...eachRateContract, 
                    ...eachRateContract.companyERPCodeMap, 
                    status: rateContractStatus.status, 
                    branchName: branchName,
                    srNo: index + 1
                }

                const downloadObj = {}

                keyNames.map(eachKey => {
                    if(updatedRateContractObj[eachKey]){
                        const columnName = rcKeyLabelMapper[eachKey]
                        downloadObj[columnName] = updatedRateContractObj[eachKey]
                    } else {
                        const columnName = rcKeyLabelMapper[eachKey]
                        downloadObj[columnName] = ""
                    }
                })
                
                rateContractProducts.push(downloadObj)
            })

            if (rateContractProducts.length === 0) {
                dispatch(raiseToast({ severity: 'warn', detail: "No data found", life: 3000 }))
            }

            if (dataFormat === "CSV") {
                let csv = Papa.unparse(rateContractProducts, { download: true })
                let blob = new Blob([csv])
                let a = window.document.createElement("a")
                a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
                a.download = "rateContract.csv"
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
            }
            if (dataFormat === "XLSX") {
                const ws = utils.json_to_sheet(rateContractProducts)
                const wb = utils.book_new()
                utils.book_append_sheet(wb, ws, 'Sheet1')
                const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'rateContract.xlsx'
                a.click()
                URL.revokeObjectURL(url)
            }
        }
    }

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    } 
    
    return (
        <div className="p-card" style={{ border: "1px solid #e9ecef" }} onKeyDown={handleSearchKeyPress}>
            <div className="grid">
                {
                    searchOptions.map(eachOption => {
                        if (eachOption.type === "SEARCH_BY") {
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)}></i>
                                        </div>
                                        :
                                        null
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name="searchBy" labelText="Search By" options={eachOption.options} value={searchParams["searchBy"]} onChange={handleSearchOnChange} />
                                    </div>
                                    {searchParams.searchBy !== "all" ?
                                        <div className="flex flex-column col-12">
                                            <TextInputField name="searchText" labelText="Search Text" value={searchParams["searchText"]} onChange={handleSearchOnChange} />
                                        </div> : null
                                    }
                                </>
                            )
                        }
                    })
                }
                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0" style={{ cursor: isPageLoading ? 'not-allowed' : 'pointer' }}>
                            <Button label="Search" style={{ flexGrow: 1 }} className="py-2" onClick={handleSearch} disabled={isPageLoading} />
                            <Menu model={downloadFormatOptions} popup ref={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3" onClick={(e) => downloadMenu.current.toggle(e)} permissions={[50]} />
                        </div>
                    </div>
                </div>
            </div>
            <Loading visible={isLoading} />
        </div>
    )
}

export default Search